import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useContext } from 'react';
import { PermissionsContext } from '../Contexts/Permissions';
import PuffLoader from "react-spinners/PuffLoader";
import Constants from '../assests/Constants/constants';
import SignatureDocumentService from '../Services/signatureDocument.service';
import UserService from '../Services/user.service';
import { isRestricted } from '../assests/Constants/menuRestrictionKeys.js';

function ProtectedRoute({
    children,
    requiredPermission = [],
    unauthorizedPermission = [],
    requireAccountant,
    preventClient = false, // Prevent all clients from accessing
    clientOnly = false, // Allow ONLY clients (block accountants/subaccountants)
    premiumClientOnly = false, // Allow only premium clients
    // Per-client ManageOnly gate: when the currently-active client has the ManageOnly
    // permission, financial/reporting/sales routes are blocked even for accountants.
    // ManageOnly clients are website-only and meant for archive + CRM only.
    preventManageOnly = false,
    validateDocumentOwnership = false, // Special check for signature documents
    restrictionMenuKey = null, // string or string[]: menu key(s) that restrict this route (block if any restricted)
}) {
    const { permissionsState, clientPermissionsState, loading, isAccountant, userTypeContext, subAccountantCaseRestrictions } = useContext(PermissionsContext);
    const location = useLocation(); // Get location dynamically
    const params = useParams(); // Get URL parameters
    const { isEdit, dynamicPermissionCheck } = location.state || {}; // Extract dynamic fields
    const [documentAuthLoading, setDocumentAuthLoading] = useState(false);
    const [documentAuthFailed, setDocumentAuthFailed] = useState(false);

    // Special authorization check for signature documents
    useEffect(() => {
        const checkDocumentOwnership = async () => {
            if (!validateDocumentOwnership || !params.documentId) {
                return;
            }

            try {
                setDocumentAuthLoading(true);
                
                // Get current user and token
                const user = await UserService.getCurrentUser();
                const token = await UserService.getCurrentToken();
                
                if (!user || !token) {
                    setDocumentAuthFailed(true);
                    return;
                }

                // Fetch the document to check ownership
                const response = await SignatureDocumentService.GetById(params.documentId, token);
                
                if (response.ok) {
                    const documentData = await response.json();
                    
                    // Verify document belongs to current user
                    if (user.caseId !== documentData.caseId) {
                        console.warn('Document ownership validation failed:', {
                            userCaseId: user.caseId,
                            documentCaseId: documentData.caseId
                        });
                        setDocumentAuthFailed(true);
                    }
                } else if (response.status === 401 || response.status === 403) {
                    console.warn('Unauthorized access to document');
                    setDocumentAuthFailed(true);
                } else {
                    console.error('Error fetching document for ownership check');
                    setDocumentAuthFailed(true);
                }
            } catch (error) {
                console.error('Error in document ownership check:', error);
                setDocumentAuthFailed(true);
            } finally {
                setDocumentAuthLoading(false);
            }
        };

        checkDocumentOwnership();
    }, [validateDocumentOwnership, params.documentId]);

    // Handle the case where permissions are not yet loaded
    if (loading || documentAuthLoading) {
        return (
            <div className="loader-container">
                <PuffLoader
                    size={100} // Larger size for better visibility
                    loading={true}
                />
            </div>
        );
    }

    // If document ownership check failed, redirect to unauthorized
    if (documentAuthFailed) {
        return <Navigate to="/unauthorized" />;
    }

    let isAllowed = true;

    // Check if the route requires the user to be an accountant
    if (requireAccountant && !isAccountant) {
        isAllowed = false;
    }

    // General prevent client logic
    if (preventClient && userTypeContext?.toString() === Constants.UserType.Client) {
        isAllowed = false;
    }

    // Client-only logic - BLOCK accountants and subaccountants
    if (clientOnly) {
        const userType = userTypeContext?.toString();
        if (userType === Constants.UserType.Accountant || userType === Constants.UserType.SubAccountant) {
            isAllowed = false;
        }
        // Only allow clients (UserType.Client = '1')
        if (userType !== Constants.UserType.Client) {
            isAllowed = false;
        }
    }

    // Premium client-only logic
    if (premiumClientOnly && userTypeContext?.toString() === Constants.UserType.Client) {
        if (!permissionsState.Premium) {
            isAllowed = false;
        }
    }

    // ManageOnly per-client gate: blocks the route when the active client carries the
    // ManageOnly permission. Driven by `clientPermissionsState`, which is keyed off the
    // active caseId in PermissionsContext.
    if (preventManageOnly && clientPermissionsState?.ManageOnly) {
        isAllowed = false;
    }

    // Check if the user has all required permissions
    if (Array.isArray(requiredPermission) && requiredPermission.length > 0) {
        const hasAllRequiredPermissions = requiredPermission.every(
            (permission) => permissionsState[permission]
        );
        isAllowed = isAllowed && hasAllRequiredPermissions;
    }

    // Handle multiple unauthorized permissions
    if (Array.isArray(unauthorizedPermission) && unauthorizedPermission.length > 0) {
        const hasUnauthorizedPermission = unauthorizedPermission.some(
            (permission) => permissionsState[permission]
        );

        // Allow access dynamically if a specific permission (like `CannotAddClient`) is present
        // and other conditions (e.g., `isEdit`) are met
        if (
            hasUnauthorizedPermission &&
            unauthorizedPermission.includes("CannotAddClient") &&
            isEdit
        ) {
            isAllowed = true;
        } else if (hasUnauthorizedPermission) {
            isAllowed = false;
        }
    }

    // Dynamic permission check from `location.state`
    if (dynamicPermissionCheck && !permissionsState[dynamicPermissionCheck]) {
        isAllowed = false;
    }

    // SubAccountant menu restrictions: block if any of this route's menu keys are restricted for the current case/global
    const restrictionKeys = restrictionMenuKey == null
        ? []
        : Array.isArray(restrictionMenuKey)
            ? restrictionMenuKey
            : [restrictionMenuKey];
    if (
        restrictionKeys.length > 0 &&
        userTypeContext?.toString() === Constants.UserType.SubAccountant &&
        restrictionKeys.some((key) => isRestricted(subAccountantCaseRestrictions || [], key))
    ) {
        isAllowed = false;
    }

    // Render children if the user is allowed, otherwise navigate to unauthorized page
    if (isAllowed) {
        return children;
    } else {
        return <Navigate to="/unauthorized" />;
    }
}

export default ProtectedRoute;
